<template>
  <div class="grabador">
    <button
      class="grabador__boton"
      :class="{ 'grabador__boton--activo': grabando, 'grabador__boton--ocupado': ocupado }"
      :disabled="ocupado"
      type="button"
      @click="alternar"
    >
      <span class="grabador__aro" :style="{ transform: `scale(${1 + nivel * 0.6})` }"></span>
      <i class="mdi" :class="grabando ? 'mdi-stop' : 'mdi-microphone'"></i>
    </button>

    <div class="grabador__texto">
      <strong v-if="grabando">Grabando… {{ reloj }}</strong>
      <strong v-else-if="ocupado">Procesando…</strong>
      <strong v-else-if="revisando">Escúchalo antes de enviar</strong>
      <strong v-else>Toca para dictar</strong>
      <small v-if="grabando">Toca de nuevo para escucharlo</small>
      <small v-else-if="revisando">{{ reloj }} grabados</small>
      <small v-else-if="!ocupado">Ej.: “muéstrame la zona de comando más cercana”</small>
    </div>
  </div>

  <!-- Revisión: la grabación no se sube hasta que la persona la aprueba. -->
  <div v-if="revisando" class="revision">
    <audio class="revision__audio" :src="urlPrevia" controls preload="auto"></audio>
    <div class="revision__acciones">
      <button class="revision__boton revision__boton--repetir" type="button" @click="descartar">
        <i class="mdi mdi-microphone"></i> Grabar de nuevo
      </button>
      <button class="revision__boton revision__boton--enviar" type="button" @click="enviar">
        <i class="mdi mdi-send"></i> Enviar
      </button>
    </div>
  </div>
</template>

<script>
import { aWav } from '@/services/audioWav';

// Graba con MediaRecorder y entrega el blob ya en WAV. El aro que late sale
// del nivel real del micrófono: se nota al tiro si no se está capturando nada.
const FORMATOS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export default {
  name: 'VoiceRecorder',
  props: {
    ocupado: { type: Boolean, default: false },
  },
  emits: ['grabado', 'error', 'inicio'],
  data() {
    return {
      grabando: false,
      segundos: 0,
      nivel: 0,
      recorder: null,
      stream: null,
      trozos: [],
      contador: null,
      audioContext: null,
      analizador: null,
      rafId: null,
      // La grabación en espera de que la persona decida.
      pendiente: null,
      urlPrevia: '',
    };
  },
  computed: {
    revisando() {
      return this.pendiente !== null && !this.ocupado;
    },

    reloj() {
      const m = String(Math.floor(this.segundos / 60)).padStart(2, '0');
      const s = String(this.segundos % 60).padStart(2, '0');
      return `${m}:${s}`;
    },
  },
  beforeUnmount() {
    this.soltarMicrofono();
    this.limpiarPrevia();
  },
  methods: {
    formatoSoportado() {
      if (typeof MediaRecorder === 'undefined') return null;
      return FORMATOS.find((f) => MediaRecorder.isTypeSupported(f)) || null;
    },

    alternar() {
      if (this.grabando) this.detener();
      else this.iniciar();
    },

    async iniciar() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$emit('error', 'Este navegador no permite grabar audio.');
        return;
      }
      const mimeType = this.formatoSoportado();
      if (!mimeType) {
        this.$emit('error', 'Este navegador no soporta MediaRecorder para audio.');
        return;
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (e) {
        this.$emit('error', 'No se pudo acceder al micrófono. Revisa los permisos del navegador.');
        return;
      }

      this.trozos = [];
      this.recorder = new MediaRecorder(this.stream, { mimeType });
      this.recorder.ondataavailable = (evento) => {
        if (evento.data && evento.data.size) this.trozos.push(evento.data);
      };
      this.recorder.onstop = async () => {
        const blob = new Blob(this.trozos, { type: mimeType });
        const duracion = this.segundos;
        this.soltarMicrofono();
        if (!blob.size) {
          this.$emit('error', 'La grabación quedó vacía.');
          return;
        }
        try {
          this.guardarParaRevisar(await aWav(blob), duracion);
        } catch (e) {
          // Sin respaldo: subir el webm igual movería el error al final del
          // pipeline, después de pagar la subida.
          this.$emit('error', `No se pudo convertir el audio a WAV: ${e.message}`);
        }
      };

      this.limpiarPrevia();
      this.recorder.start();
      this.grabando = true;
      this.segundos = 0;
      this.contador = setInterval(() => { this.segundos += 1; }, 1000);
      this.medirNivel();
      this.$emit('inicio');
    },

    guardarParaRevisar(wav, duracion) {
      this.limpiarPrevia();
      this.pendiente = { wav, duracion };
      this.urlPrevia = URL.createObjectURL(wav);
    },

    enviar() {
      const { wav, duracion } = this.pendiente;
      this.limpiarPrevia();
      this.$emit('grabado', wav, duracion);
    },

    descartar() {
      this.limpiarPrevia();
      this.iniciar();
    },

    limpiarPrevia() {
      // El object URL se suelta a mano: si no, el blob queda vivo hasta que se
      // recargue la página.
      if (this.urlPrevia) URL.revokeObjectURL(this.urlPrevia);
      this.urlPrevia = '';
      this.pendiente = null;
    },

    detener() {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
      this.grabando = false;
      if (this.contador) {
        clearInterval(this.contador);
        this.contador = null;
      }
    },

    medirNivel() {
      try {
        const Contexto = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new Contexto();
        const fuente = this.audioContext.createMediaStreamSource(this.stream);
        this.analizador = this.audioContext.createAnalyser();
        this.analizador.fftSize = 512;
        fuente.connect(this.analizador);

        const datos = new Uint8Array(this.analizador.frequencyBinCount);
        const leer = () => {
          if (!this.analizador) return;
          this.analizador.getByteTimeDomainData(datos);
          let suma = 0;
          for (let i = 0; i < datos.length; i += 1) {
            const v = (datos[i] - 128) / 128;
            suma += v * v;
          }
          this.nivel = Math.min(1, Math.sqrt(suma / datos.length) * 4);
          this.rafId = requestAnimationFrame(leer);
        };
        leer();
      } catch (e) {
        this.nivel = 0; // el medidor es cosmético: si falla, se graba igual
      }
    },

    soltarMicrofono() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
      if (this.audioContext) {
        this.audioContext.close().catch(() => {});
        this.audioContext = null;
      }
      this.analizador = null;
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      if (this.contador) {
        clearInterval(this.contador);
        this.contador = null;
      }
      this.nivel = 0;
      this.grabando = false;
    },
  },
};
</script>
